import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarProvider,
} from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { cn } from "@arcadeai/design-system/lib/utils";
import { useLocation } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import Logo from "@/assets/images/arcade-logo.svg";
import { data } from "@/components/custom/sidebar/data";
import { dashboardSidebarStyle } from "@/components/custom/sidebar/sidebar-style";
import type { SidebarItem } from "@/components/custom/sidebar/types";

// Where sign-in lands: `/` redirects to the playground chat. public/theme-init.js
// repeats it for the static loader; app-shell-skeleton.test.ts keeps them equal.
export const CHAT_LANDING_PATH =
  /^\/(orgs\/[^/]+\/projects\/[^/]+\/playground\/chat(\/.*)?)?$/;

export const isChatLandingPath = (pathname: string) =>
  CHAT_LANDING_PATH.test(pathname);

// Set once the user has reached the dashboard layout, cleared on sign-out, on a
// redirect to login, and when `/` sends them to onboarding. Without it the
// loading UI stays blank instead of drawing a frame the visitor is not about to
// see. Cosmetic only: auth is enforced by the API. public/theme-init.js reads it
// for the static loader.
export const SHELL_HINT_KEY = "arcade-dashboard-shell";

export const setShellHint = (on: boolean) => {
  try {
    if (on) {
      localStorage.setItem(SHELL_HINT_KEY, "1");
    } else {
      localStorage.removeItem(SHELL_HINT_KEY);
    }
  } catch {
    // Storage can be blocked by browser policy; the loading UI stays blank.
  }
};

const hasShellHint = () => {
  try {
    return localStorage.getItem(SHELL_HINT_KEY) === "1";
  } catch {
    return false;
  }
};

const NAV_SECTIONS = [
  data.navOverview,
  data.navAgentDeveloper,
  data.navToolDeveloper,
  data.navSecurity,
];
const SUGGESTION_ROWS = [
  ["s1", "s2", "s3"],
  ["s4", "s5"],
];

// Org-only entries show to org admins alone, so most users never see them.
const isShownToMostUsers = (item: Pick<SidebarItem, "requiredOrgPermission">) =>
  !item.requiredOrgPermission;

const labelWidth = (title: string): CSSProperties => ({
  width: `${title.length * 0.45}rem`,
});

const NavItemSkeleton = ({ item }: { item: SidebarItem }) => (
  <SidebarMenuItem>
    <div className="flex h-8 items-center gap-2 px-2">
      <Skeleton className="size-4 rounded-md" />
      <Skeleton className="h-4" style={labelWidth(item.title)} />
    </div>
    {!!item.items?.length && (
      <SidebarMenuSub className="w-full">
        {item.items.filter(isShownToMostUsers).map((subItem) => (
          <SidebarMenuSubItem className="w-full pr-4" key={subItem.id}>
            <div className="flex h-7 items-center px-2">
              <Skeleton className="h-4" style={labelWidth(subItem.title)} />
            </div>
          </SidebarMenuSubItem>
        ))}
      </SidebarMenuSub>
    )}
  </SidebarMenuItem>
);

// Spaced like the org/project switchers: ghost buttons whose padding insets
// the icon and name, each followed by a "/" separator.
const SwitcherSkeleton = ({ nameClassName }: { nameClassName: string }) => (
  <div className="flex h-7 items-center gap-2 px-2.75">
    <Skeleton className="size-4 rounded" />
    <Skeleton className={cn("h-4", nameClassName)} />
    <Skeleton className="size-3.5" />
  </div>
);

const CrumbSeparatorSkeleton = () => (
  <Skeleton className="mr-3 ml-1 h-4 w-1.5" />
);

const PlaygroundChatSkeleton = () => (
  <div className="flex h-full min-h-0 flex-col gap-4">
    <Skeleton className="h-9 w-44 rounded-lg" />
    <div className="flex items-center gap-2 px-2">
      <Skeleton className="h-9 w-26" />
      <Skeleton className="h-9 w-42" />
      <Skeleton className="ml-auto h-9 w-23" />
    </div>
    <div className="flex min-h-0 flex-1 flex-col items-center gap-4 pt-16">
      <Skeleton className="h-9 w-56" />
      <Skeleton className="h-6 w-xl max-w-full" />
      <div className="mt-auto flex w-full max-w-3xl flex-col gap-4">
        {SUGGESTION_ROWS.map((row) => (
          <div className="flex gap-4" key={row.join()}>
            {row.map((key) => (
              <div
                className="flex h-29 flex-1 flex-col gap-3 rounded-xl border p-4"
                key={key}
              >
                <Skeleton className="size-8 rounded-lg" />
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="h-3 w-2/5" />
              </div>
            ))}
          </div>
        ))}
        <div className="h-29 rounded-xl border" />
      </div>
    </div>
  </div>
);

/**
 * Frame of the dashboard layout, shown while the session, org/project context
 * and capabilities resolve. Sidebar rows follow the shape of the nav most users
 * see but carry no labels, links or icons: the real items depend on
 * capabilities that have not loaded yet. The content area previews the
 * playground chat only when that is the destination; any other page shows its
 * own skeleton once it renders, so the area stays empty until then.
 */
export const AppShellSkeleton = () => {
  const isChatLanding = useLocation({
    select: ({ pathname }) => isChatLandingPath(pathname),
  });
  if (!hasShellHint()) {
    return <div aria-busy="true" className="h-svh bg-background" />;
  }
  return (
    <div aria-busy="true" className="flex h-svh flex-col">
      <SidebarProvider
        className="min-h-0 w-svw max-w-full flex-1"
        style={dashboardSidebarStyle}
      >
        <Sidebar
          className="overflow-x-hidden"
          collapsible="icon"
          variant="inset"
        >
          <SidebarHeader>
            <img
              alt="Arcade"
              className="-mb-[0.05rem] h-8 w-fit object-cover px-1 pt-0.5 pb-1.5 invert dark:invert-0"
              height="32"
              src={Logo}
              width="98"
            />
          </SidebarHeader>
          <SidebarContent className="overflow-x-hidden">
            <div className="mask-b-from-60% mt-2">
              {NAV_SECTIONS.map((items) => (
                <SidebarGroup key={items[0]?.id}>
                  <SidebarMenu>
                    {items.filter(isShownToMostUsers).map((item) => (
                      <NavItemSkeleton item={item} key={item.id} />
                    ))}
                  </SidebarMenu>
                </SidebarGroup>
              ))}
            </div>
          </SidebarContent>
          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <div className="flex h-8 items-center gap-2 px-2">
                  <Skeleton className="size-4 rounded-md" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </SidebarMenuItem>
              <SidebarMenuItem className="my-3">
                <Skeleton className="h-8 w-full rounded-md" />
              </SidebarMenuItem>
            </SidebarMenu>
            <div className="flex h-12 items-center gap-2 p-2">
              <Skeleton className="size-8 rounded-lg" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
          </SidebarFooter>
        </Sidebar>
        <SidebarInset className="min-h-0 overflow-hidden">
          <header className="mb-2.5 flex min-h-(--header-height) shrink-0 items-center gap-2 border-b">
            <div className="flex w-full items-center justify-between gap-2 px-4 py-2">
              <div className="flex h-8 items-center gap-2">
                <Skeleton className="-ml-1 size-7" />
                <Skeleton className="mr-1.5 h-4 w-px" />
                <div className="flex items-center">
                  <SwitcherSkeleton nameClassName="w-10" />
                  <CrumbSeparatorSkeleton />
                  <SwitcherSkeleton nameClassName="w-24" />
                  <CrumbSeparatorSkeleton />
                  <Skeleton className="h-4 w-18" />
                  <Skeleton className="mr-1.5 ml-2 size-3.5" />
                  <Skeleton className="h-4 w-8" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Skeleton className="h-5 w-18" />
                <Skeleton className="size-8" />
              </div>
            </div>
          </header>
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden px-6 py-2">
            {isChatLanding && <PlaygroundChatSkeleton />}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};
